import { useEffect, useState } from "react";
import { api, apiError, CITIES } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Sparkles, Loader2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1612687286248-9811801bd618?w=1000";

export function corporateFee(employees) {
  const n = Number(employees) || 0;
  if (n <= 50) return 50;
  if (n <= 100) return 75;
  return 100;
}

export default function Corporate() {
  const [trucks, setTrucks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    contact_name: "", company: "", email: "", phone: "", event_date: "",
    location: "Green Bay", employees: 50, budget: "", details: "", truck_id: "any",
  });

  useEffect(() => { api.get("/trucks").then((r) => setTrucks(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const fee = corporateFee(form.employees);
  const [ai, setAi] = useState({ loading: false, reply: "", truck_ids: [] });
  const suggest = async () => {
    if (!form.details) { toast.error("Add a few event details first"); return; }
    setAi((a) => ({ ...a, loading: true }));
    try {
      const { data } = await api.post("/ai/corporate-assistant", { description: form.details, employees: Number(form.employees) || 1, city: form.location });
      setAi({ loading: false, reply: data.reply, truck_ids: data.truck_ids || [] });
    } catch (e) { toast.error(apiError(e)); setAi((a) => ({ ...a, loading: false })); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        employees: Number(form.employees) || 1,
        guests: Number(form.employees) || 1,
        budget: form.budget ? Number(form.budget) : null,
        truck_id: form.truck_id === "any" ? null : form.truck_id,
      };
      const { data } = await api.post("/corporate-bookings", payload);
      const pay = await api.post(`/corporate-bookings/${data.id}/pay`, { origin_url: window.location.origin });
      window.location.href = pay.data.checkout_url;
    } catch (err) { toast.error(apiError(err)); setSubmitting(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-12 grid lg:grid-cols-2 gap-10 items-start">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600 flex items-center gap-2"><Building2 className="w-4 h-4" /> Corporate Bookings</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Bring a food truck to your office.</h1>
        <p className="text-slate-500 mt-3 text-lg leading-relaxed">Company party, employee appreciation day, or product launch? Pay a small booking fee and local trucks will get your request and reach out with quotes.</p>
        <div className="rounded-2xl overflow-hidden mt-8 gr-shadow">
          <img src={HERO} alt="Corporate event" className="w-full h-64 object-cover" />
        </div>
        <div className="mt-6 bg-white rounded-2xl border border-teal-100 p-5" data-testid="corp-fee-table">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Booking fee by team size</p>
          <div className="grid grid-cols-3 gap-3 text-center">
            <FeeCell active={fee === 50} size="Up to 50" price="$50" />
            <FeeCell active={fee === 75} size="51–100" price="$75" />
            <FeeCell active={fee === 100} size="100+" price="$100" />
          </div>
          <p className="text-xs text-slate-400 mt-3">The truck that serves your event pays FlavorFlow a 10% commission on their sales — never you.</p>
        </div>
      </div>

      <form onSubmit={submit} className="bg-white rounded-2xl border border-teal-100 p-7 gr-shadow space-y-4" data-testid="corporate-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Your name"><Input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="corp-name" /></Field>
          <Field label="Company"><Input required value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="corp-company" /></Field>
          <Field label="Email"><Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="corp-email" /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="corp-phone" /></Field>
          <Field label="Event date"><Input type="date" required value={form.event_date} onChange={(e) => set("event_date", e.target.value)} data-testid="corp-date" /></Field>
          <Field label="Number of employees"><Input type="number" min="1" value={form.employees} onChange={(e) => set("employees", e.target.value)} data-testid="corp-employees" /></Field>
          <Field label="City">
            <Select value={form.location} onValueChange={(v) => set("location", v)}>
              <SelectTrigger data-testid="corp-city"><SelectValue /></SelectTrigger>
              <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Budget (optional)"><Input type="number" placeholder="$" value={form.budget} onChange={(e) => set("budget", e.target.value)} data-testid="corp-budget" /></Field>
        </div>
        <Field label="Preferred truck (optional)">
          <Select value={form.truck_id} onValueChange={(v) => set("truck_id", v)}>
            <SelectTrigger data-testid="corp-truck"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any available truck</SelectItem>
              {trucks.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.cuisine})</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Details"><Textarea rows={3} value={form.details} onChange={(e) => set("details", e.target.value)} placeholder="Tell us about your event…" data-testid="corp-details" /></Field>
        <div className="rounded-xl border border-teal-100 p-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-[#0FB5A6]" /> Not sure who to book?</span>
            <Button type="button" onClick={suggest} disabled={ai.loading} variant="outline" className="rounded-full h-8 text-xs font-bold border-teal-200" data-testid="ai-suggest-btn">{ai.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} Suggest trucks with AI</Button>
          </div>
          {ai.reply && (
            <div className="mt-3 text-sm text-slate-700 whitespace-pre-line" data-testid="ai-suggest-result">
              {ai.reply}
              {ai.truck_ids.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {ai.truck_ids.map((id) => { const t = trucks.find((x) => x.id === id); return t ? <a key={id} href={`/truck/${id}`} className="px-3 py-1 rounded-full bg-teal-50 text-[#0FB5A6] text-xs font-bold hover:bg-teal-100">{t.name}</a> : null; })}
                </div>
              )}
            </div>
          )}
        </div>
        <Button type="submit" disabled={submitting} className="w-full rounded-full h-12 font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white active:scale-95 transition-transform" data-testid="corp-submit">
          {submitting ? "Redirecting to checkout…" : `Pay $${fee} & request quotes`}
        </Button>
        <p className="text-xs text-center text-slate-400">Secure payment via Stripe. Your request is published to local trucks after payment.</p>
      </form>
    </div>
  );
}

const FeeCell = ({ active, size, price }) => (
  <div className={`rounded-xl border p-3 ${active ? "border-[#0FB5A6] bg-teal-50" : "border-teal-100"}`}>
    <p className="text-xs text-slate-500">{size}</p>
    <p className={`font-display text-xl font-black ${active ? "text-[#0FB5A6]" : "text-slate-700"}`}>{price}</p>
  </div>
);

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
  </div>
);
